import { colors } from "@styles/colors";
import type { BillDetail, BillListItem, BillPaymentCodes, BillType } from "../API/handleBillsAPI";

/** 帳單種類 */
export const billTypeLabel = (type: BillType): string => (type === "normal_device_bill" ? "瓦斯表帳單" : "月結帳單");

export interface BillStatusMeta {
  label: string;
  fg: string;
  bg: string;
}

/**
 * 客戶視角的狀態。已過繳費期限還沒繳完的，優先顯示「已逾期」——比「未繳費」更需要客戶注意。
 * 後台用字是「未結帳／部分結帳／已結帳」（瓦斯行的角度），這裡改成客戶的角度。
 */
export const billStatusMeta = (bill: Pick<BillListItem, "status" | "is_overdue">): BillStatusMeta => {
  if (bill.status === "paid") return { label: "已繳清", fg: colors.greenText, bg: colors.bgGreen };
  if (bill.is_overdue) return { label: "已逾期", fg: colors.redText, bg: colors.bgRed };
  if (bill.status === "partially_paid") return { label: "部分繳費", fg: colors.amberDark, bg: colors.bgAmber };
  return { label: "未繳費", fg: colors.amberDark, bg: colors.bgAmber };
};

/** YYYY-MM-DD → YYYY/MM/DD */
export const ymdSlash = (ymd: string | null | undefined): string => (ymd ? ymd.replace(/-/g, "/") : "");

/**
 * 帳期顯示：同一年省略迄日年份（2026/09/01 – 09/30）。
 * 帳單的帳期已經是台灣日期字串，不再經過 Date 轉時區。
 */
export const billPeriodLabel = (start: string, end: string): string => {
  const s = ymdSlash(start);
  const e = ymdSlash(end);
  if (start.slice(0, 4) === end.slice(0, 4)) return `${s} – ${e.slice(5)}`;
  return `${s} – ${e}`;
};

/** 帳期所屬月份（以迄日為準），列表標題用：2026 年 9 月 */
export const billMonthLabel = (end: string): string => {
  const [y, m] = end.split("-");
  return `${y} 年 ${Number(m)} 月`;
};

export const money = (n: number): string => `$ ${Math.round(n).toLocaleString()}`;

/** 收款時間（ISO）→ 本地 YYYY/MM/DD */
export const paidAtLabel = (iso: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const p = (v: number) => String(v).padStart(2, "0");
  return `${d.getFullYear()}/${p(d.getMonth() + 1)}/${p(d.getDate())}`;
};

/** 超商條碼產不出來時給客戶的說明；invalid_setting 是瓦斯行設定問題，不對客戶多說，直接不顯示超商區塊 */
export const cvsUnavailableText = (reason: BillPaymentCodes["cvs_unavailable_reason"]): string | null => {
  if (reason === "over_limit") return "金額超過 2 萬元，超商無法代收，請使用虛擬帳號轉帳。";
  if (reason === "expired") return "超商繳費期限已過，請使用虛擬帳號轉帳，或聯絡瓦斯行索取新的繳費資訊。";
  return null;
};

/**
 * 收款單方向（bill_of_sale_work.direction：收款／付款／沖抵）。
 * 「付款」是瓦斯行把錢退給客戶，「沖抵」是拿其他款項抵掉，都要標出來，不然客戶會以為是自己付的錢。
 */
export const paymentDirectionPrefix = (direction: string): string => {
  if (direction === "付款") return "退款 ";
  if (direction === "沖抵") return "沖抵 ";
  return "";
};

/** 虛擬帳號每 4 碼空一格，方便對照 ATM 畫面輸入 */
export const groupDigits = (value: string): string => value.replace(/(\d{4})(?=\d)/g, "$1 ");

export interface BillTotalRow {
  label: string;
  value: string;
  emphasis?: boolean;
}

/**
 * 合計區（同後台帳單列印）：本期合計 → 樓層費 → 折扣 → 已收 → 上期欠款 → 應付總計。
 * 樓層費、上期欠款是 null 代表瓦斯行設定不計入，整列不顯示。
 */
export const billTotalRows = (totals: BillDetail["totals"]): BillTotalRow[] => {
  const rows: BillTotalRow[] = [{ label: "本期合計", value: money(totals.subtotal) }];
  if (totals.floor_fee !== null) rows.push({ label: "樓層費", value: `+ ${money(totals.floor_fee)}` });
  if (totals.discount > 0) rows.push({ label: "折扣", value: `− ${money(totals.discount)}` });
  if (totals.paid > 0) rows.push({ label: "已收金額", value: `− ${money(totals.paid)}` });
  if (totals.prior_owed !== null && totals.prior_owed !== 0) {
    rows.push({ label: "上期欠款", value: money(totals.prior_owed) });
  }
  rows.push({ label: "應付總計", value: money(totals.amount_due), emphasis: true });
  return rows;
};
